/**
 * What Foundry's data does not say about who casts how (docs/adr/0003): each caster class's
 * tradition, casting type, spellcasting attribute and where it prepares from; the tradition and
 * attribute of the focus spells non-caster classes get; the archetype spellcasting progression
 * (Player Core p. 215, as the Archetypes journal states it); the divine font's slot counts. The
 * slot tables themselves come from the Classes journal at build time (config.spellcasting.slots).
 */
import type { CastingType, MagicTradition } from "../character/model";
import type { Attribute } from "./values";

export interface ClassCasting {
  /** A fixed tradition, or "feature" when a granted feature names it (a bloodline's Spell List, a patron's, an eidolon's Tradition). */
  tradition: MagicTradition | "feature";
  casting: Extract<CastingType, "prepared" | "spontaneous">;
  /** A fixed attribute, or "key" for the class's key attribute. */
  attribute: Attribute | "key";
  /** Prepared casters prepare from a spellbook of spells known, or from every common spell of their tradition. */
  preparesFrom: "spellbook" | "tradition";
  /** Cantrips a multiclass dedication of this class prepares or knows. */
  dedicationCantrips: number;
  /** A second caster the class's table folds into its cells (the animist's apparitions), with the label of the lists that fill it. */
  secondary?: { id: string; name: string; casting: Extract<CastingType, "prepared" | "spontaneous">; listLabel: string };
}

/** Keyed by class slug. Every class with a Spells per Day table in the Classes journal must appear here. */
export const CLASS_CASTING: Readonly<Record<string, ClassCasting>> = {
  animist: { tradition: "divine", casting: "prepared", attribute: "wis", preparesFrom: "tradition", dedicationCantrips: 2, secondary: { id: "apparition", name: "Apparition Spells", casting: "spontaneous", listLabel: "apparition spells" } },
  bard: { tradition: "occult", casting: "spontaneous", attribute: "cha", preparesFrom: "tradition", dedicationCantrips: 2 },
  cleric: { tradition: "divine", casting: "prepared", attribute: "wis", preparesFrom: "tradition", dedicationCantrips: 2 },
  druid: { tradition: "primal", casting: "prepared", attribute: "wis", preparesFrom: "tradition", dedicationCantrips: 2 },
  magus: { tradition: "arcane", casting: "prepared", attribute: "int", preparesFrom: "spellbook", dedicationCantrips: 2 },
  necromancer: { tradition: "occult", casting: "prepared", attribute: "int", preparesFrom: "spellbook", dedicationCantrips: 2 },
  oracle: { tradition: "divine", casting: "spontaneous", attribute: "cha", preparesFrom: "tradition", dedicationCantrips: 2 },
  psychic: { tradition: "occult", casting: "spontaneous", attribute: "key", preparesFrom: "tradition", dedicationCantrips: 2 },
  sorcerer: { tradition: "feature", casting: "spontaneous", attribute: "cha", preparesFrom: "tradition", dedicationCantrips: 2 },
  summoner: { tradition: "feature", casting: "spontaneous", attribute: "cha", preparesFrom: "tradition", dedicationCantrips: 2 },
  witch: { tradition: "feature", casting: "prepared", attribute: "int", preparesFrom: "spellbook", dedicationCantrips: 2 },
  wizard: { tradition: "arcane", casting: "prepared", attribute: "int", preparesFrom: "spellbook", dedicationCantrips: 2 },
};

/** Focus spells of classes that do not cast otherwise: their tradition and spellcasting attribute (Player Core, Player Core 2). */
export const FOCUS_OWNERS: Readonly<Record<string, { tradition: MagicTradition; attribute: Attribute }>> = {
  champion: { tradition: "divine", attribute: "cha" },
  monk: { tradition: "divine", attribute: "wis" },
  ranger: { tradition: "primal", attribute: "wis" },
  thaumaturge: { tradition: "occult", attribute: "cha" },
  exemplar: { tradition: "divine", attribute: "cha" },
  barbarian: { tradition: "primal", attribute: "cha" },
  rogue: { tradition: "arcane", attribute: "cha" },
};

/** The feature-list tags of features that name a class's tradition (Spell List / Tradition labels). */
export const TRADITION_FEATURE_TAGS = ["sorcerer-bloodline", "witch-patron", "summoner-eidolon"] as const;

/** The tag Foundry puts on multiclass spellcasting dedications. */
export const SPELLCASTING_DEDICATION_TAG = "spellcasting-multiclass-archetype";

/**
 * Archetype spellcasting benefits by Character Level (the Archetypes journal, "Spellcasting
 * Archetypes"): the basic feat grants a 1st-rank slot at 4, 2nd at 6, 3rd at 8; the expert feat
 * 4th at 12, 5th at 14, 6th at 16; the master feat 7th at 18, 8th at 20.
 */
export const ARCHETYPE_SLOT_LEVELS: Readonly<Record<"basic" | "expert" | "master", readonly { rank: number; level: number }[]>> = {
  basic: [{ rank: 1, level: 4 }, { rank: 2, level: 6 }, { rank: 3, level: 8 }],
  expert: [{ rank: 4, level: 12 }, { rank: 5, level: 14 }, { rank: 6, level: 16 }],
  master: [{ rank: 7, level: 18 }, { rank: 8, level: 20 }],
};

/** The divine font's slots at the highest rank (Player Core: 4, then 5 at 5th level and 6 at 15th). */
export function divineFontSlots(level: number): number {
  return level >= 15 ? 6 : level >= 5 ? 5 : 4;
}

export const HEAL_UUID = "Compendium.pf2e.spells-srd.Item.rfZpqmj0AIIdkVIs";
export const HARM_UUID = "Compendium.pf2e.spells-srd.Item.wdA52JJnsuQWeyqz";

/** Slot counts per rank for a class at a Level, from the built table: index 0 cantrips, 1 to 10 ranks. */
export function classSlots(table: { cantrips: number[]; slots: number[][] } | undefined, level: number): number[] {
  const at = Math.min(20, Math.max(1, level)) - 1;
  if (!table) return Array(11).fill(0);
  return [table.cantrips[at] ?? 0, ...Array.from({ length: 10 }, (_, i) => table.slots[at]?.[i] ?? 0)];
}

/** The highest rank with a slot. 0 when only cantrips. */
export function highestRank(slots: readonly number[]): number {
  for (let rank = 10; rank >= 1; rank--) if ((slots[rank] ?? 0) > 0) return rank;
  return 0;
}

export const TRADITION_LABELS: Readonly<Record<MagicTradition, string>> = { arcane: "Arcane", divine: "Divine", occult: "Occult", primal: "Primal" };
export const CASTING_LABELS: Readonly<Record<CastingType, string>> = { prepared: "Prepared", spontaneous: "Spontaneous", innate: "Innate", focus: "Focus", items: "Staff" };
